import type { SerializableNode, TContentGateAttributes } from '@shared/dream-components';

import type { Subscription } from '~/interfaces/subscriptions';

import getCookieValue from './getCookieValue';

type Arguments = {
  contentGateNode: SerializableNode | null;
  currentTierId: string | null;
  cookiesHeader: string | null;
  subscription?: Subscription | null;
};

export const shouldClipAfterContentGate = ({
  contentGateNode,
  cookiesHeader,
  currentTierId,
  subscription,
}: Arguments) => {
  if (!contentGateNode || 'attrs' in contentGateNode === false) {
    return false;
  }

  const nodeAttributes = contentGateNode.attrs as TContentGateAttributes;

  const isLoggedIn = Boolean(subscription);
  const isPremiumUser = subscription?.tier === 'premium' && subscription.status !== 'paused';
  const viewingAsNewVisitor = currentTierId === 'none';
  const hasUserSubscribed = getCookieValue('email_capture_disabled', cookiesHeader || '') === 'true';

  switch (nodeAttributes.showWhen) {
    case 'paid': {
      if (!subscription || !isLoggedIn || (isLoggedIn && !isPremiumUser)) {
        return true;
      }

      const showWhenWithoutTierIds: string[] = nodeAttributes.showWhenWithoutTierIds || [];
      // Find tiers the user is subscribed to that are not paused
      const subscriptionTierIds: string[] =
        subscription?.premium_tiers?.filter((tier) => !tier.paused)?.map((tier) => tier.id) || [];

      if (showWhenWithoutTierIds.length === 0) {
        return true;
      }

      // Current in the website editor it is not possible to select multiple tiers
      // so we can safely assume that if the first tier in showWhenWithoutTierIds is
      // not in the subscriptionTierIds, we should clip the content.
      if (!subscriptionTierIds.includes(showWhenWithoutTierIds[0])) {
        return true;
      }

      return false;
    }
    case 'free':
      return !isLoggedIn || (isLoggedIn && !isPremiumUser);
    case 'unsubscribed': {
      // The cookie `email_capture_disabled` is set in signup action
      if (hasUserSubscribed || isLoggedIn) {
        return false;
      }

      if (currentTierId) {
        return viewingAsNewVisitor;
      }

      return true;
    }
    default:
      return false;
  }
};
